import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { ImageInterface } from "@core/interfaces/image.interface";
import { DeviceService } from "@core/services/device.service";
import { ImageService } from "@core/services/image/image.service";
import { FilterAcquisitionService, FilterSummary } from "@features/equipment/services/filter-acquisition.service";

interface EquipmentSummary {
  telescopes: { name: string; count: number }[];
  cameras: { name: string; count: number }[];
  filterSummary: string;
  totalIntegration: string;
}

@Component({
  selector: "astrobin-presentation-info-panel",
  templateUrl: "./presentation-info-panel.component.html",
  styleUrls: ["./presentation-info-panel.component.scss"]
})
export class PresentationInfoPanelComponent implements OnChanges {
  @Input() image: ImageInterface;
  @Input() showControls = true;
  @Output() visitImage = new EventEmitter<void>();

  title: string;
  photographer: string;
  photographerLabel: string;
  publicationDate: Date;
  equipmentSummary: EquipmentSummary;

  get showViewButton(): boolean {
    return !this.deviceService.mdMin() || this.showControls;
  }

  constructor(
    private readonly imageService: ImageService,
    private readonly filterAcquisitionService: FilterAcquisitionService,
    private readonly deviceService: DeviceService
  ) {}

  ngOnChanges(): void {
    if (this.image) {
      this.updateImageInfo();
    }
  }

  formatEquipmentItem(item: { name: string; count: number }): string {
    return item.count > 1 ? `${item.name} (+${item.count - 1})` : item.name;
  }

  hasEquipment(): boolean {
    return (
      this.equipmentSummary &&
      (this.equipmentSummary.telescopes.length > 0 ||
        this.equipmentSummary.cameras.length > 0 ||
        !!this.equipmentSummary.filterSummary)
    );
  }

  private updateImageInfo(): void {
    this.title = this.image.title || "";
    this.photographer = this.getPhotographerInfo();
    this.photographerLabel = this.image.collaborators?.length > 0 ? "Photographers" : "Photographer";
    this.publicationDate = this.image.published ? new Date(this.image.published) : null;
    this.equipmentSummary = this.buildEquipmentSummary();
  }

  private getPhotographerInfo(): string {
    const names = [this.image.userDisplayName];

    if (this.image.collaborators?.length > 0) {
      const collaboratorNames = this.image.collaborators.map(c =>
        typeof c === "string" ? c : c.displayName || c.username || "Unknown"
      );
      names.push(...collaboratorNames);
    }

    return names.join(", ");
  }

  private buildEquipmentSummary(): EquipmentSummary {
    const summary: EquipmentSummary = {
      telescopes: [],
      cameras: [],
      filterSummary: "",
      totalIntegration: ""
    };

    if (this.image.imagingTelescopes2?.length > 0) {
      summary.telescopes = this.groupEquipment(this.image.imagingTelescopes2, "telescope");
    } else if (this.image.imagingTelescopes?.length > 0) {
      summary.telescopes = this.groupEquipment(this.image.imagingTelescopes, "telescope");
    }

    if (this.image.imagingCameras2?.length > 0) {
      summary.cameras = this.groupEquipment(this.image.imagingCameras2, "camera");
    } else if (this.image.imagingCameras?.length > 0) {
      summary.cameras = this.groupEquipment(this.image.imagingCameras, "camera");
    }

    const filterSummariesMap = this.filterAcquisitionService.buildFilterSummaries(this.image);
    summary.filterSummary = this.generateFilterAcronym(filterSummariesMap);

    // Calculate total integration in seconds
    let totalIntegrationSeconds = 0;

    if (this.image.deepSkyAcquisitions?.length > 0) {
      totalIntegrationSeconds = this.image.deepSkyAcquisitions
        .map(acq => (acq.number || 0) * parseFloat(acq.duration || "0"))
        .reduce((acc, val) => acc + val, 0);
    } else if (this.image.solarSystemAcquisitions?.length > 0) {
      // For solar system, we might want to show frames x exposure instead of total time
      const acquisition = this.image.solarSystemAcquisitions[0];
      if (acquisition.frames && acquisition.exposurePerFrame) {
        // Convert milliseconds to seconds for solar system
        totalIntegrationSeconds = (acquisition.frames * acquisition.exposurePerFrame) / 1000;
      }
    }

    if (totalIntegrationSeconds > 0) {
      summary.totalIntegration = this.imageService.formatIntegration(totalIntegrationSeconds, false);
    }

    return summary;
  }

  private groupEquipment(equipment: any[], type: string): { name: string; count: number }[] {
    const grouped = new Map<string, number>();

    equipment.forEach(item => {
      let label: string;
      if (typeof item === "string") {
        label = item;
      } else if (item.brandName && item.name) {
        label = `${item.brandName} ${item.name}`;
      } else if (item.make && item.name) {
        label = `${item.make} ${item.name}`;
      } else if (item.name) {
        label = item.name;
      } else {
        label = "Unknown";
      }
      grouped.set(label, (grouped.get(label) || 0) + 1);
    });

    return Array.from(grouped.entries()).map(([name, count]) => ({ name, count }));
  }

  private generateFilterAcronym(filterSummariesMap: { [key: string]: FilterSummary }): string {
    if (Object.keys(filterSummariesMap).length === 0) {
      return "";
    }

    // Get sorted filter summaries which include filterType
    const sortedSummaries = this.filterAcquisitionService.getSortedFilterSummaries(filterSummariesMap);
    const filterTypes = sortedSummaries.map(f => f.filterType).filter(type => type && type !== "UNKNOWN");
    const uniqueTypes = new Set(filterTypes);

    // If all filters are UNKNOWN, return empty string
    if (uniqueTypes.size === 0) {
      return "";
    }

    if (uniqueTypes.has("L") && uniqueTypes.has("R") && uniqueTypes.has("G") && uniqueTypes.has("B")) {
      const hasNarrowband = uniqueTypes.has("H_ALPHA") || uniqueTypes.has("O_III") || uniqueTypes.has("S_II");
      return hasNarrowband ? "LRGB+" : "LRGB";
    }

    if (uniqueTypes.has("H_ALPHA") && uniqueTypes.has("O_III") && !uniqueTypes.has("S_II")) {
      return "HOO";
    }

    if (uniqueTypes.has("H_ALPHA") && uniqueTypes.has("S_II") && uniqueTypes.has("O_III")) {
      return "SHO";
    }

    if (Array.from(uniqueTypes).some(type => type?.includes("LP") || type?.includes("CLS"))) {
      return "LP";
    }

    const typeMap = {
      H_ALPHA: "Ha",
      O_III: "OIII",
      S_II: "SII",
      L: "L",
      R: "R",
      G: "G",
      B: "B"
    };

    return Array.from(uniqueTypes)
      .map(type => typeMap[type] || this.filterAcquisitionService.humanizeFilterType(type))
      .join(" + ");
  }
}
